/** Sprint 3A API hooks: "Nu betalen", payer, archived companies, invite codes (ops). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BillingDTO, EntityDTO, OpsInviteCodeDTO } from '@btw/shared';
import { qk } from '../../api/hooks';
import { get, post } from '../../api/client';

/** Every view that depends on the plan status (session, period pages, Home). */
function refreshPlan(qc: ReturnType<typeof useQueryClient>, b: BillingDTO) {
  qc.setQueryData(qk.billing, b);
  return Promise.all(['me', 'overview', 'period', 'periods'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

/** "Nu betalen": the open first payment or a failed invoice. Redirects to the hosted payment page when there is one. */
export function usePayNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ checkoutUrl: string | null; billing: BillingDTO }>('/billing/pay'),
    onSuccess: async (r) => {
      if (r.checkoutUrl) {
        window.location.assign(r.checkoutUrl);
        return;
      }
      await refreshPlan(qc, r.billing);
    },
  });
}

export function useSetPayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entityId: string) => post<BillingDTO>('/billing/payer', { entityId }),
    onSuccess: (b) => qc.setQueryData(qk.billing, b),
  });
}

export const archivedKey = ['entities', 'archived'] as const;

export function useArchivedEntities(enabled = true) {
  return useQuery({ queryKey: archivedKey, queryFn: ({ signal }) => get<EntityDTO[]>('/entities/archived', undefined, signal), enabled });
}

/** Archive / restore: lists, Home, periods and billing all change. */
export function useArchiveEntity(action: 'archive' | 'unarchive') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<EntityDTO>(`/entities/${id}/${action}`),
    onSuccess: () => Promise.all(['entities', 'overview', 'periods', 'billing'].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

export const useOpsInviteCodes = () => useQuery({ queryKey: qk.ops('invite-codes'), queryFn: () => get<OpsInviteCodeDTO[]>('/ops/invite-codes') });

export function useCreateInviteCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { maxUses: number; expiresOn: string; note?: string }) => post<{ code: string; invite: OpsInviteCodeDTO }>('/ops/invite-codes', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.ops('invite-codes') }),
  });
}

export function useRevokeInviteCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<{ ok: true }>(`/ops/invite-codes/${id}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.ops('invite-codes') }),
  });
}
